import React, { useMemo } from 'react'
import { sevStroke } from './segments'
import type { WorldFlowHop, WorldFlowLink } from '@/analysis/worldFlowTypes'
import type { WorldPoint } from '@/analysis/apiTypes'
import { useStore } from '@/store'
import { segmentSevForNode, type WorldSegment } from './segments'
import { hopKey, type PosMap } from './hopPlacement'

export { buildHopPositions, hopKey, countriesNeededForPlacement, continentsNeededForPlacement } from './hopPlacement'
export type { PosMap } from './hopPlacement'

const ARC_DRAW_CSS = `
  @keyframes arcDraw {
    from { stroke-dashoffset: 1; }
    to   { stroke-dashoffset: 0; }
  }
  /* Explicitly kill any inherited land path fill (chord / semicircle). */
  .world-arc-draw,
  .world-arc-hit {
    fill: none !important;
    fill-opacity: 0 !important;
  }
  .world-arc-draw {
    stroke-dasharray: 1;
    stroke-dashoffset: 1;
    animation: arcDraw 700ms ease-out forwards;
  }
`

export type ClusterArc = {
  id: string
  fromCluster: string
  toCluster: string
  usersTotal: number
  totalStreams: number
  avgSeverity: 0 | 1 | 2 | null
  worstMetric: string | null
  metrics: unknown
  label: string
}

interface Props {
  links: WorldFlowLink[]
  positions: PosMap
  zoom: number
  pulseEpoch: number
  segment: WorldSegment
  mapToScreen: (x: number, y: number) => { x: number; y: number }
  onHoverEnter: (p: WorldPoint, x: number, y: number) => void
  onHoverLeave: (p: WorldPoint) => void
  hops?: WorldFlowHop[]
  clusterArcs?: ClusterArc[]
}

/** Subtle bow — stroke only, never closed / filled. */
function arcPath(x0: number, y0: number, x1: number, y1: number): string {
  const mx = (x0 + x1) / 2
  const my = (y0 + y1) / 2
  const dx = x1 - x0
  const dy = y1 - y0
  const len = Math.hypot(dx, dy) || 1
  const bow = Math.min(18, len * 0.06)
  const cx = mx - (dy / len) * bow
  const cy = my + (dx / len) * bow
  return `M ${x0} ${y0} Q ${cx} ${cy} ${x1} ${y1}`
}

function linkLabel(link: WorldFlowLink, hops: WorldFlowHop[]): string {
  const fromHop = hops.find((h) => h.type === link.from.type && h.value === link.from.value)
  const toHop = hops.find((h) => h.type === link.to.type && h.value === link.to.value)
  const from = fromHop?.label ?? link.from.value
  const to = toHop?.label ?? link.to.value
  return `${from} → ${to}`
}

export function PipelineArcs({
  links, positions, zoom, pulseEpoch, segment,
  mapToScreen, onHoverEnter, onHoverLeave, hops = [], clusterArcs,
}: Props) {
  const setFilterOnly = useStore((s) => s.setFilterOnly)
  const setClientGeoFilter = useStore((s) => s.setClientGeoFilter)
  const worldClientGrain = useStore((s) => s.worldClientGrain)
  const setDimension = useStore((s) => s.setDimension)
  const setView = useStore((s) => s.setView)
  const descendToStreams = useStore((s) => s.descendToStreams)

  const arcs = useMemo(() => {
    const out: Array<{
      id: string
      d: string
      mid: { x: number; y: number }
      sev: 0 | 1 | 2 | null
      label: string
      delayMs: number
      strokeScale: number
      totalStreams: number
      usersTotal: number
      avgSeverity: 0 | 1 | 2 | null
      worstMetric: string | null
      metrics: unknown
      hopLink: WorldFlowLink | null
    }> = []
    let i = 0

    if (clusterArcs) {
      for (const link of clusterArcs) {
        const fromPos = positions[link.fromCluster]
        const toPos = positions[link.toCluster]
        if (!fromPos || !toPos) continue
        const sev = segmentSevForNode(
          { avgSeverity: link.avgSeverity, metrics: link.metrics as never },
          segment,
        )
        const d = arcPath(fromPos.x, fromPos.y, toPos.x, toPos.y)
        const strokeScale = link.totalStreams > 0
          ? Math.min(2.4, 0.7 + Math.sqrt(link.totalStreams) * 0.22)
          : 0.4
        out.push({
          id: link.id,
          d,
          mid: { x: (fromPos.x + toPos.x) / 2, y: (fromPos.y + toPos.y) / 2 },
          sev,
          label: link.label,
          delayMs: (i++ % 10) * 60,
          strokeScale,
          totalStreams: link.totalStreams,
          usersTotal: link.usersTotal,
          avgSeverity: link.avgSeverity,
          worstMetric: link.worstMetric,
          metrics: link.metrics,
          hopLink: null,
        })
      }
      return out
    }

    for (const link of links) {
      const fromPos = positions[hopKey(link.from.type, link.from.value)]
      const toPos = positions[hopKey(link.to.type, link.to.value)]
      if (!fromPos || !toPos) continue
      const sev = segmentSevForNode(
        { avgSeverity: link.avgSeverity, metrics: link.metrics },
        segment,
      )
      const d = arcPath(fromPos.x, fromPos.y, toPos.x, toPos.y)
      const strokeScale = link.totalStreams > 0
        ? Math.min(2.4, 0.7 + Math.sqrt(link.totalStreams) * 0.22)
        : 0.4
      out.push({
        id: `${link.from.type}:${link.from.value}->${link.to.type}:${link.to.value}`,
        d,
        mid: { x: (fromPos.x + toPos.x) / 2, y: (fromPos.y + toPos.y) / 2 },
        sev,
        label: linkLabel(link, hops),
        delayMs: (i++ % 10) * 60,
        strokeScale,
        totalStreams: link.totalStreams,
        usersTotal: link.usersTotal,
        avgSeverity: link.avgSeverity,
        worstMetric: link.worstMetric,
        metrics: link.metrics,
        hopLink: link,
      })
    }
    return out
  }, [links, positions, segment, hops, clusterArcs])

  const hitW = Math.max(6, 10 / Math.max(zoom, 0.001))

  return (
    <g key={`pipeline-arcs-${pulseEpoch}`}>
      <style>{ARC_DRAW_CSS}</style>
      {arcs.map((arc) => {
        const inactive = arc.totalStreams <= 0
        const color = inactive ? 'rgba(180,190,210,0.85)' : sevStroke(arc.sev)
        const healthy = !inactive && (arc.sev === 2 || arc.sev == null)
        const opacity = inactive ? 0.2 : healthy ? 0.5 : 0.95
        const screen = mapToScreen(arc.mid.x, arc.mid.y)
        const strokeW = Math.max(1, Math.min(2.2, arc.strokeScale / Math.max(zoom, 0.001)))

        const point: WorldPoint = {
          id: `link:${arc.id}`,
          label: arc.label,
          lat: 0,
          lon: 0,
          total: arc.totalStreams,
          crit: 0,
          warn: 0,
          sev: arc.sev ?? 2,
          usersTotal: arc.usersTotal,
          avgSeverity: arc.avgSeverity,
          worstMetric: arc.worstMetric,
          metrics: arc.metrics,
        } as WorldPoint

        const onClick = (e: React.MouseEvent) => {
          e.stopPropagation()
          const link = arc.hopLink
          if (!link) {
            setDimension('stream')
            setView('hex')
            return
          }
          const { from, to } = link
          if (from.type === 'studio') {
            descendToStreams('studio', from.value)
          } else {
            setFilterOnly('cdnEdge', from.value)
          }
          if (to.type === 'cdn_edge') {
            setFilterOnly('cdnEdge', to.value)
          } else {
            // Client hop — grain decides filter key (continent values must not become city).
            const parts = to.value.split('|')
            if (worldClientGrain === 'continent') {
              setClientGeoFilter({ grain: 'continent', continent: to.value })
            } else if (
              worldClientGrain === 'country' ||
              (parts.length !== 2 && /^[A-Za-z]{2}$/.test(to.value))
            ) {
              setClientGeoFilter({ grain: 'country', country: to.value.toUpperCase() })
            } else if (parts.length === 2) {
              setClientGeoFilter({ grain: 'city', city: parts[0]!, country: parts[1]! })
            } else {
              setClientGeoFilter({ grain: 'city', city: to.value })
            }
          }
          setDimension('stream')
          setView('hex')
        }

        return (
          <g
            key={arc.id}
            style={{ cursor: 'pointer' }}
            onClick={onClick}
            onPointerEnter={() => onHoverEnter(point, screen.x, screen.y)}
            onPointerLeave={() => onHoverLeave(point)}
          >
            <path
              className="world-arc-hit"
              d={arc.d}
              fill="none"
              stroke="transparent"
              strokeWidth={hitW}
            />
            <path
              className="world-arc-draw"
              d={arc.d}
              fill="none"
              stroke={color}
              strokeOpacity={opacity}
              strokeWidth={strokeW}
              strokeLinecap="round"
              strokeLinejoin="round"
              pathLength={1}
              style={{ animationDelay: `${arc.delayMs}ms` }}
            />
            {(arc.sev === 0 || arc.sev === 1) && !inactive && (
              <circle r={Math.max(1.2, 2.2 / Math.max(zoom, 0.001))} fill={color} opacity={0.95}>
                <animateMotion dur="1.8s" repeatCount="indefinite" path={arc.d} />
              </circle>
            )}
          </g>
        )
      })}
    </g>
  )
}
